// ===========================================
// 86 BOARD
// ===========================================
// The kitchen's kill switch: mark an item 86'd (out) and every POS greys it
// out within seconds; mark it back when it returns. Search, tap, done.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Search, Ban, RotateCcw } from 'lucide-react';
import { kitchenApi } from '../services/api';

interface MenuItem {
  id: string;
  name: string;
  isAvailable: boolean;
}

export default function EightySixModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const { data: items, isLoading } = useQuery({
    queryKey: ['kds-menu-items'],
    queryFn: async () => {
      const response = await kitchenApi.getMenuItems();
      return (response.data?.data || []) as MenuItem[];
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async (item: MenuItem) => {
      if (item.isAvailable) {
        await kitchenApi.markUnavailable(item.id, "Kitchen 86'd");
      } else {
        await kitchenApi.markAvailable(item.id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kds-menu-items'] });
    },
  });

  const filtered = (items || []).filter((i) =>
    i.name.toLowerCase().includes(search.toLowerCase())
  );
  // 86'd items float to the top so the current kill list is always visible
  const sorted = [...filtered].sort(
    (a, b) => Number(a.isAvailable) - Number(b.isAvailable) || a.name.localeCompare(b.name)
  );

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-2xl w-full max-w-md shadow-2xl flex flex-col max-h-[85dvh]">
        <div className="px-5 py-4 border-b border-slate-700 flex items-center justify-between flex-shrink-0">
          <h3 className="font-bold text-white flex items-center gap-2">
            <Ban className="w-5 h-5 text-red-400" />
            86 Board
          </h3>
          <button onClick={onClose} className="p-2 rounded-lg active:bg-slate-700">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="p-4 flex-shrink-0">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Find an item…"
              className="w-full pl-9 pr-3 py-2.5 bg-slate-700 text-white rounded-xl placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 pb-4" style={{ WebkitOverflowScrolling: 'touch' }}>
          {isLoading ? (
            <p className="text-center text-slate-500 py-8">Loading menu…</p>
          ) : sorted.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No items match</p>
          ) : (
            <div className="space-y-2">
              {sorted.map((item) => (
                <button
                  key={item.id}
                  onClick={() => toggleMutation.mutate(item)}
                  disabled={toggleMutation.isPending}
                  className={
                    item.isAvailable
                      ? 'w-full flex items-center justify-between px-4 py-3 rounded-xl bg-slate-700/60 text-slate-200 active:bg-slate-700 disabled:opacity-50'
                      : 'w-full flex items-center justify-between px-4 py-3 rounded-xl bg-red-900/40 border border-red-800 text-red-200 active:bg-red-900/60 disabled:opacity-50'
                  }
                >
                  <span className="font-medium text-left">{item.name}</span>
                  {item.isAvailable ? (
                    <span className="flex items-center gap-1.5 text-xs text-red-300 font-semibold">
                      <Ban className="w-4 h-4" /> 86 it
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-xs text-emerald-300 font-semibold">
                      <RotateCcw className="w-4 h-4" /> Bring back
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
